import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Trophy, Sword, TennisBall, CalendarCheck, Clock } from "@phosphor-icons/react";

/** Aggregated history of matches/retas/rentals/tournaments for a club, optionally per-court.
 * Lets user filter by type and court. Cross-links each item to its detail page.
 *
 * Props:
 *   clubId, courtName? (optional), allowCourtPicker? (default true)
 */
export default function CourtHistory({ clubId, courtName, allowCourtPicker = true, courtNames = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterCourt, setFilterCourt] = useState(courtName || "all");

  useEffect(() => {
    if (!clubId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (filterCourt && filterCourt !== "all") params.set("court_name", filterCourt);
    params.set("limit", "60");
    api.get(`/clubs/${clubId}/court-history?${params.toString()}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [clubId, filterCourt]);

  const filtered = filterType === "all" ? items : items.filter((i) => i.type === filterType);

  const typeLabel = { match: "Marcador", reta: "Reta", rental: "Renta", tournament: "Torneo" };
  const typeIcon = { match: TennisBall, reta: Sword, rental: CalendarCheck, tournament: Trophy };
  const typeColor = { match: "text-cyan-400 border-cyan-400/30 bg-cyan-400/10", reta: "text-orange-400 border-orange-400/30 bg-orange-400/10", rental: "text-blue-400 border-blue-400/30 bg-blue-400/10", tournament: "text-purple-400 border-purple-400/30 bg-purple-400/10" };
  const linkFor = (it) => ({ match: `/dashboard/marcador/${it.id}`, reta: `/dashboard/retas`, rental: `/dashboard/canchas`, tournament: `/dashboard/torneos` }[it.type]);

  return (
    <div className="card-tactical p-5" data-testid="court-history">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock size={18} weight="duotone" className="text-[#CCFF00]" />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">
            Historial {filterCourt !== "all" ? `· ${filterCourt}` : "del club"}
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-gray-500">{filtered.length} eventos</span>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {[{ k: "all", l: "Todos" }, { k: "match", l: "Marcadores" }, { k: "reta", l: "Retas" }, { k: "rental", l: "Rentas" }, { k: "tournament", l: "Torneos" }].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilterType(f.k)}
            className={`px-2 h-7 text-[10px] font-bold uppercase tracking-wider border ${filterType === f.k ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/10 hover:border-[#CCFF00]/30"}`}
            data-testid={`history-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
        {allowCourtPicker && courtNames.length > 0 && (
          <select
            value={filterCourt}
            onChange={(e) => setFilterCourt(e.target.value)}
            className="bg-[#0A0A0A] border border-white/10 text-white text-[10px] font-bold uppercase px-2 h-7 cursor-pointer hover:border-[#CCFF00]/30 ml-auto"
            data-testid="history-court-filter"
          >
            <option value="all">Todas las canchas</option>
            {courtNames.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-[11px] text-gray-500 text-center py-6">Sin eventos en el historial.</p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {filtered.map((it) => {
            const Icon = typeIcon[it.type] || TennisBall;
            return (
              <Link
                key={`${it.type}-${it.id}`}
                to={linkFor(it)}
                className="bg-[#0A0A0A] border border-white/5 hover:border-[#CCFF00]/30 px-3 py-2 flex items-center gap-3 transition-colors"
                data-testid={`history-item-${it.type}-${it.id}`}
              >
                <div className={`w-8 h-8 flex items-center justify-center border ${typeColor[it.type]} shrink-0`}>
                  <Icon size={14} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[9px] ${typeColor[it.type]}`}>{typeLabel[it.type]}</Badge>
                    {it.court_name && <span className="text-[10px] text-gray-500 uppercase tracking-wider">{it.court_name}</span>}
                    {it.category && <span className="text-[10px] text-[#CCFF00]">Cat. {it.category}</span>}
                    {it.reta_day && <span className="text-[10px] text-cyan-400 capitalize">{it.reta_day}</span>}
                  </div>
                  <p className="text-xs text-white truncate mt-0.5">{it.title}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-['JetBrains_Mono']">{it.date}{it.time ? ` ${it.time}` : ""}</p>
                  {it.amount != null && <p className="text-[10px] text-[#CCFF00] font-bold">${it.amount}</p>}
                  {it.status && <p className="text-[9px] text-gray-500 uppercase">{it.status}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
